import React, { FC, useEffect, useMemo, useState } from 'react'
import { ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js'
import './wallet-options.css'

// Capacitor
import { Capacitor } from '@capacitor/core'
import { ApplePayEventsEnum } from '@capacitor-community/stripe'
import { useCapacitorStripe } from '@capacitor-community/stripe/react'

// Components
import Button from '@components/atoms/button'

// Types
import type { StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js'

// Utils
import { min } from '@/_constants/constants'
import { StripeAPI } from '@apis'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    groupId: string
    amount: number

    onConfirm: (intentId: string, paymentMethod?: CreditCard | ApplePay | GooglePay) => void
    enterZero?: boolean
}

const WalletOptions: FC<Props> = (props) => {
    const { groupId, amount, onConfirm, enterZero } = props

    const { stripe: capacitorStripe, isApplePayAvailable } = useCapacitorStripe()
    const stripe = useStripe()
    const elements = useElements()

    const [isSubmitting, setIsSubmitting] = useState(false)

    const invalidAmount = enterZero ? (amount < ((amount > 0 && amount < 100) ? min : 0)) : (amount < min)

    // When ever the amount changes we want to update the elements hook config
    useEffect(() => {
        if (amount > 0) {
            elements?.update({
                mode: 'payment',
                currency: 'usd',
                amount: Math.max(amount, 100),
                capture_method: 'manual',
                setup_future_usage: 'off_session'
            })
        } else {
            elements?.update({
                mode: 'setup',
                currency: 'usd',
                setup_future_usage: 'off_session',
            })
        }
    }, [amount, amount > 0])


    const platform = useMemo(() => {
        return Capacitor.getPlatform() as 'ios' | 'android' | 'web'
    }, [Capacitor.getPlatform()])

    useEffect(() => {
        async function init() {
            await capacitorStripe.initialize({
                publishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
            })
        }

        if (platform !== 'web')
            init()
    }, [platform, platform !== 'web'])

    async function handleExpress(event: StripeExpressCheckoutElementConfirmEvent) {
        if (!stripe || !elements) throw new Error("failed to load Stripe")

        await elements.submit()
        console.log('On confirm: ', event)

        const { clientSecret } = await StripeAPI.CreateSetupIntentRequest.fetch({
            type: event.expressPaymentType as any,
            intentType: amount > 0 ? 'payment_intent' : 'setup_intent',
            amount
        })

        if (amount > 0) {
            const { paymentIntent, error } = await stripe.confirmPayment({
                elements,
                clientSecret,
                confirmParams: {
                    return_url: new URL(`/group/${groupId}`, document.location.href).toString()
                },
                redirect: 'if_required'
            })
            if (error) throw error

            const paymentMethod = await StripeAPI.GetPaymentMethodRequest.fetch(paymentIntent.payment_method as string)
                .catch((err) => {
                    console.error(err)
                    return {} as CreditCard
                })

            onConfirm(paymentIntent.id, paymentMethod)
        } else {
            const { setupIntent, error } = await stripe.confirmSetup({
                elements,
                clientSecret,
                confirmParams: {
                    return_url: new URL(`/group/${groupId}`, document.location.href).toString()
                },
                redirect: 'if_required'
            })

            if (error) throw error

            const paymentMethod = await StripeAPI.GetPaymentMethodRequest.fetch(setupIntent.payment_method as string)
                .catch((err) => {
                    console.error(err)
                    return {} as CreditCard
                })

            onConfirm(setupIntent.id, paymentMethod)
        }
    }

    async function handleApplePay() {
        setIsSubmitting(true)

        try {
            const { clientSecret, paymentId } = await StripeAPI.CreateSetupIntentRequest.fetch({
                type: 'apple_pay',
                intentType: amount > 0 ? 'payment_intent' : 'setup_intent',
                amount
            })

            await capacitorStripe.createApplePay({
                paymentIntentClientSecret: clientSecret,
                paymentSummaryItems: [
                    { label: 'Splitcart LLC', amount: amount / 100 }
                ],
                merchantIdentifier: import.meta.env.VITE_APPLE_PAY_MERCHANT_ID,
                currency: 'USD',
                countryCode: 'US'
            })

            const { paymentResult } = await capacitorStripe.presentApplePay()
            console.log('presentApplePay: ', paymentResult)

            if (paymentResult === ApplePayEventsEnum.Completed) {
                if (!paymentId) throw new Error('Payment intent id was undefined')
                onConfirm(paymentId!, undefined)
            }
        } catch (error) {
            console.error("issue handling apple pay", error)
        }

        setIsSubmitting(false)
    }

    return (
        <>
            {(platform == 'web' && (enterZero || amount > 0)) &&
                <ExpressCheckoutElement
                    options={{
                        layout: { maxRows: 1, maxColumns: 2 },
                        paymentMethods: { applePay: 'always', googlePay: 'always' }
                    }}
                    onConfirm={handleExpress}
                    onCancel={(event) => { console.log("On Cancel:", event) }}
                    onLoadError={(err) => { console.error("On Load Error:", err) }}
                    onReady={(event) => { console.log("On Ready:", event) }}
                    onClick={({ resolve }) => {
                        resolve({
                            phoneNumberRequired: true,
                            emailRequired: true,
                        })
                    }}
                />
            }
            {isApplePayAvailable &&
                <Button
                    style={{
                        backgroundColor: 'var(--splitcard-black)',
                        color: 'white',
                        borderColor: 'var(--splitcard-black)',
                        opacity: isSubmitting || invalidAmount ? 0.7 : 1,
                    }}
                    theme="primary"
                    disabled={isSubmitting || invalidAmount}
                    onClick={handleApplePay}
                >
                    <div className='flex flex-row items-center justify-center' style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                        <img src="/icons/apple-logo.svg" alt="Apple" style={{ marginRight: '0.5rem' }} />
                        <span style={{ fontSize: '1.2em', fontWeight: '600' }}>Pay</span>
                    </div>
                </Button>
            }
        </>
    )
}

export default WalletOptions
